import * as yup from "yup";

export const schema = () => {
  return yup
    .object({
      promo: yup.string(),
      comments: yup.string(),
    })
    .required();
};

export const flattenAndSort = (config: any) => {
  const flatConfig: {
    key: string;
    label: string;
    order: number;
    path: string;
  }[] = [];

  for (const [groupKey, groupValue] of Object.entries(config)) {
    if ("key" in groupValue) {
      flatConfig.push({ ...groupValue, path: groupKey });
    } else {
      for (const [key, value] of Object.entries(groupValue)) {
        flatConfig.push({ ...value, path: `${groupKey}.${key}` });
      }
    }
  }

  return flatConfig.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const humanizeFormKey = (key: string) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

const orderTypeFieldForKey = (orderTypeForm: any, key: string) => {
  if (!orderTypeForm || typeof orderTypeForm !== "object") return null;
  if (orderTypeForm[key]?.type === "radioSelect") return orderTypeForm[key];
  if (
    key === "orderType" &&
    orderTypeForm.radioSelect?.type === "radioSelect"
  ) {
    return orderTypeForm.radioSelect;
  }
  return null;
};

const quoteRecipientCommentKeys = new Set([
  "radioSelect",
  "recipientName",
  "recipientEmail",
  "phoneNumber",
  "countryCode",
  "recipientId",
  "recipient_company_name",
  "recipient_company_location",
]);

export const omitQuoteRecipientCommentFields = (data: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(data ?? {}).filter(
      ([key]) => !quoteRecipientCommentKeys.has(key)
    )
  );

export const formatPersonalisationText = (
  data: Record<string, any>,
  commentTitle?: string,
  additionalForm?: any,
  options?: {
    orderTypeForm?: any;
    translate?: (key: string, fallback?: string) => string;
  }
): string => {
  let lines: string[] = [];

  if (commentTitle) {
    lines.push(`${commentTitle}`);
  } else {
    lines.push("-");
  }

  Object.entries(data).forEach(([key, value]) => {
    if (value == null || typeof value === "object") return;
    let label = humanizeFormKey(key);
    const orderTypeField = orderTypeFieldForKey(options?.orderTypeForm, key);

    if (orderTypeField) {
      if (orderTypeField.label && options?.translate) {
        label = options.translate(orderTypeField.label, label);
      } else if (key === "orderType") {
        label = options?.translate
          ? options.translate(
              "requestHistoryDetail.orderType.text",
              "Order Type"
            )
          : "Order Type";
      }
    } else if (key === "radioSelect" && additionalForm) {
      const radioFieldKey = Object.keys(additionalForm).find(
        (k: string) => additionalForm[k].type === "radioSelect"
      );
      if (radioFieldKey) {
        label = humanizeFormKey(radioFieldKey);
      }
    }

    lines.push(`    ${label}: "${value ?? ""}"`);
  });

  return lines.join("\n");
};

export const getScaledResolution = (
  screenWidth: number,
  screenHeight: number
) => {
  const MAX_WIDTH = 853;
  const MAX_HEIGHT = 640;

  let scale;

  if (screenWidth <= 1280) {
    scale = 0.5;
  } else if (screenWidth <= 1440) {
    scale = 0.5 + ((screenWidth - 1280) / (1440 - 1280)) * (0.555 - 0.5);
  } else {
    const scaleStep = (0.555 - 0.5) / (1440 - 1280);
    scale = 0.555 + (screenWidth - 1440) * scaleStep;
  }

  // Initial candidate from width
  let candidateWidth = Math.round(screenWidth * scale);
  let candidateHeight = Math.round((3 / 4) * candidateWidth);

  // Check height fit
  if (candidateHeight > screenHeight) {
    candidateHeight = screenHeight;
    candidateWidth = Math.round((4 / 3) * candidateHeight);
  }

  // Final check: cap at 853x640 max
  if (candidateWidth > MAX_WIDTH && candidateHeight > MAX_HEIGHT) {
    candidateWidth = MAX_WIDTH;
    candidateHeight = MAX_HEIGHT;
  }

  return {
    width: candidateWidth,
    height: candidateHeight,
  };
};
